import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import debounce from 'lodash/debounce';

import { AutocompleteV2ViewModel } from 'shared/components/autocomplete-v2';
import { HttpClient } from 'shared/libs/http';
import { AsyncState } from 'shared/libs/state';
import { ApiResponseDto } from 'shared/types';

export type CountryCityCountry = {
  id: number | string;
  name: string;
};

export type CountryCityCity = {
  id: number | string;
  name: string;
  region?: string;
};

export type CountryCitySearchResponse = ApiResponseDto<CountryCityCity[]>;

export type CountryCitySelectorStoreState = {
  countryId?: number | string;
  cityId?: number | string | null;
  cityLabel?: string;
  endpoint?: string;
};

export class CountryCitySelectorStore {
  countryId = '';
  cityLabel = '';

  readonly cityOptions = observable.array<CountryCityCity>([]);
  readonly cityVM = new AutocompleteV2ViewModel<CountryCityCity>({
    inputValue: '',
  });
  readonly citySearchState = new AsyncState<CountryCitySearchResponse>();

  private activeCitySearchRequestId: number | null = null;
  private citySearchRequestId = 0;
  private readonly endpoint: string;
  private readonly debouncedFetchCities = debounce((query: string) => {
    this.getCities(query);
  }, 500);

  constructor({
    countryId = '',
    cityId = null,
    cityLabel = '',
    endpoint = '/locality/search',
  }: CountryCitySelectorStoreState = {}) {
    this.countryId = String(countryId);
    this.cityLabel = cityLabel;
    this.endpoint = endpoint;

    makeObservable(this, {
      countryId: observable,
      cityLabel: observable,
      cityOptions: observable,
      isCityLoading: computed,
      cityAutocompleteOptions: computed,
      setCountryId: action.bound,
      setCity: action.bound,
      setCityLabel: action.bound,
      onSearchCity: action.bound,
      onSelectCity: action.bound,
      clearCity: action.bound,
    });

    this.cityVM.setInputValue(this.cityLabel);
    if (cityId !== null && cityId !== undefined && cityLabel) {
      this.cityVM.setValue({
        id: cityId,
        name: cityLabel,
      });
    }
  }

  get cityAutocompleteOptions(): CountryCityCity[] {
    return this.cityOptions.slice();
  }

  get isCityLoading(): boolean {
    return (
      this.citySearchState.status === 'pending' &&
      this.activeCitySearchRequestId !== null
    );
  }

  setCountryId(value: number | string): void {
    const nextCountryId = String(value);

    if (this.countryId === nextCountryId) {
      return;
    }

    this.countryId = nextCountryId;
    this.clearCity();
  }

  setCity(value: { id?: number | string | null; label: string }): void {
    this.cityLabel = value.label;
    this.cityVM.setInputValue(value.label);
    this.cityVM.setValue(
      value.id === null || value.id === undefined
        ? null
        : {
            id: value.id,
            name: value.label,
          }
    );
  }

  setCityLabel(value: string): void {
    this.cityLabel = value;
    this.cityVM.setInputValue(value);
  }

  onSearchCity(query: string): void {
    this.cityLabel = query;
    if (
      !this.cityVM.value ||
      !sameCityLabel(formatCountryCityLabel(this.cityVM.value), query)
    ) {
      this.cityVM.setValue(null);
    }
    this.cityVM.setInputValue(query);

    if (!this.countryId || query.length < 2) {
      this.debouncedFetchCities.cancel();
      this.citySearchRequestId += 1;
      this.activeCitySearchRequestId = null;
      this.cityOptions.clear();
      return;
    }

    this.debouncedFetchCities(query);
  }

  onSelectCity(item: CountryCityCity): void {
    const cityLabel = formatCountryCityLabel(item);

    this.debouncedFetchCities.cancel();
    this.citySearchRequestId += 1;
    this.activeCitySearchRequestId = null;
    this.cityLabel = cityLabel;
    this.cityVM.select(item, cityLabel);
  }

  clearCity(): void {
    this.debouncedFetchCities.cancel();
    this.citySearchRequestId += 1;
    this.activeCitySearchRequestId = null;
    this.cityLabel = '';
    this.cityOptions.clear();
    this.cityVM.setValue(null);
    this.cityVM.setInputValue('');
  }

  dispose(): void {
    this.debouncedFetchCities.cancel();
  }

  private async getCities(query: string): Promise<void> {
    if (!this.countryId) {
      return;
    }

    const requestId = ++this.citySearchRequestId;
    this.activeCitySearchRequestId = requestId;

    await this.citySearchState.run(
      () => HttpClient.post<CountryCitySearchResponse>(
        this.endpoint,
        {
          country_id: Number(this.countryId),
          query,
        }
      ),
      {
        onSuccess: (response) => {
          runInAction(() => {
            if (requestId === this.activeCitySearchRequestId && response.status === 200) {
              this.cityOptions.replace(response.response);
              const exactMatch = response.response.find((city) => {
                return (
                  sameCityLabel(formatCountryCityLabel(city), this.cityVM.inputValue) ||
                  sameCityLabel(city.name, this.cityVM.inputValue)
                );
              });

              if (exactMatch) {
                this.cityVM.setValue(exactMatch);
              }
            }
          });
        },
        onError: () => {
          runInAction(() => {
            if (requestId === this.activeCitySearchRequestId) {
              this.cityOptions.clear();
            }
          });
        },
        onFinally: () => {
          runInAction(() => {
            if (requestId === this.activeCitySearchRequestId) {
              this.activeCitySearchRequestId = null;
            }
          });
        },
      }
    );
  }
}

export function formatCountryCityLabel(city: CountryCityCity): string {
  return `${city.name}${city.region ? `, ${city.region}` : ''}`;
}

function sameCityLabel(left: string, right: string): boolean {
  return left.trim().toLowerCase() === right.trim().toLowerCase();
}
